/** Prédicats plans du contrôle du fuseau utilisateur, à la manière de shapely (GEOS) : ce que
 *  `user_corridor.validate` demande à `LineString.is_simple`, `intersects`, `Polygon.is_valid` et
 *  `Polygon.distance`, plus la simplification de Douglas-Peucker du serveur. Coordonnées planes quelconques
 *  (degrés [lon, lat] ou mètres d'un plan local) : les prédicats ne mesurent pas, ils comparent. */

export type XY = readonly [number, number];

/** Signe de l'aire du triangle (a, b, c) : > 0 à gauche de a → b. */
function orient(a: XY, b: XY, c: XY): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/** c, colinéaire au segment [a, b], tombe dans sa boîte. */
function onSegment(a: XY, b: XY, c: XY): boolean {
  return (
    Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1])
  );
}

/** Segments fermés [a, b] et [c, d] : intersection ou contact. */
export function segmentsIntersect(a: XY, b: XY, c: XY, d: XY): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0)) && ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0))) return true;
  return (
    (o1 === 0 && onSegment(a, b, c)) ||
    (o2 === 0 && onSegment(a, b, d)) ||
    (o3 === 0 && onSegment(c, d, a)) ||
    (o4 === 0 && onSegment(c, d, b))
  );
}

const same = (a: XY, b: XY) => a[0] === b[0] && a[1] === b[1];

/** Points sans répétition consécutive (GEOS les ignore). */
function dedupe(points: readonly XY[]): XY[] {
  const out: XY[] = [];
  for (const p of points) if (!out.length || !same(out[out.length - 1]!, p)) out.push(p);
  return out;
}

/** Deux segments consécutifs (a → v, v → b) qui repartent l'un sur l'autre (pointe, recouvrement). */
function spike(a: XY, v: XY, b: XY): boolean {
  return orient(a, v, b) === 0 && (a[0] - v[0]) * (b[0] - v[0]) + (a[1] - v[1]) * (b[1] - v[1]) > 0;
}

/** Premier recoupement d'une polyligne (null ⇔ `LineString.is_simple`) : les deux segments en faute (pointe :
 *  les deux segments consécutifs), chacun par les indices dans `points` de ses deux sommets ; null si simple.
 *  Hors du point commun de deux segments consécutifs (et des extrémités d'une polyligne fermée). */
export function selfFault(points: readonly XY[]): [[number, number], [number, number]] | null {
  // Sommets sans répétition consécutive, avec leur rang d'origine (GEOS ignore les répétitions).
  const p: XY[] = [];
  const rank: number[] = [];
  points.forEach((q, k) => {
    if (!p.length || !same(p[p.length - 1]!, q)) {
      p.push(q);
      rank.push(k);
    }
  });
  const n = p.length - 1; // segments
  if (n < 2) return null;
  const closed = same(p[0]!, p[n]!);
  const fault = (i: number, j: number): [[number, number], [number, number]] => [
    [rank[i]!, rank[i + 1]!],
    [rank[j]!, rank[j + 1]!],
  ];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1) {
        if (spike(p[i]!, p[i + 1]!, p[i + 2]!)) return fault(i, j);
      } else if (closed && i === 0 && j === n - 1) {
        if (spike(p[n - 1]!, p[0]!, p[1]!)) return fault(i, j);
      } else if (segmentsIntersect(p[i]!, p[i + 1]!, p[j]!, p[j + 1]!)) {
        return fault(i, j);
      }
    }
  }
  return null;
}

/** `LineString(a).intersects(LineString(b))` : un segment de l'une touche un segment de l'autre. Indices
 *  (i, j) des premiers segments en contact, ou null. */
export function polylinesIntersect(a: readonly XY[], b: readonly XY[]): [number, number] | null {
  const pa = a.length === 1 ? [a[0]!, a[0]!] : a;
  const pb = b.length === 1 ? [b[0]!, b[0]!] : b;
  for (let i = 0; i + 1 < pa.length; i++)
    for (let j = 0; j + 1 < pb.length; j++) if (segmentsIntersect(pa[i]!, pa[i + 1]!, pb[j]!, pb[j + 1]!)) return [i, j];
  return null;
}

/** Premier contact interdit d'un contour fermé (`Polygon(ring).is_valid` faux) : indices des deux segments
 *  du contour (sans répétition), [i, i] pour un contour de moins de trois sommets distincts ; null si valide.
 *  Le contour est fermé implicitement. */
export function ringFault(points: readonly XY[]): [number, number] | null {
  const p = dedupe([...points, points[0]!]);
  const n = p.length - 1;
  if (n < 3) return [0, 0];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1) {
        if (spike(p[i]!, p[j]!, p[j + 1]!)) return [i, j];
      } else if (i === 0 && j === n - 1) {
        if (spike(p[n - 1]!, p[0]!, p[1]!)) return [i, j];
      } else if (segmentsIntersect(p[i]!, p[i + 1]!, p[j]!, p[j + 1]!)) {
        return [i, j];
      }
    }
  }
  return null;
}

/** Point dans un contour fermé (règle pair-impair). */
function inside(ring: readonly XY[], p: XY): boolean {
  let found = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) found = !found;
  }
  return found;
}

function segmentDistance(p: XY, a: XY, b: XY): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length2 = dx * dx + dy * dy;
  const f = length2 > 0 ? Math.min(Math.max(((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / length2, 0), 1) : 0;
  return Math.hypot(p[0] - a[0] - f * dx, p[1] - a[1] - f * dy);
}

/** `Polygon(ring).distance(Point(p))` : 0 à l'intérieur, sinon distance au contour (fermé implicitement). */
export function polygonDistance(ring: readonly XY[], p: XY): number {
  if (inside(ring, p)) return 0;
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) best = Math.min(best, segmentDistance(p, ring[i]!, ring[(i + 1) % ring.length]!));
  return best;
}

/** Douglas-Peucker dans le plan, extrémités gardées (`user_corridor._douglas_peucker`) : indices gardés. */
export function douglasPeucker(points: readonly XY[], tolerance: number): number[] {
  if (points.length < 3) return points.map((_, i) => i);
  const keep = points.map(() => false);
  keep[0] = keep[points.length - 1] = true;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const [ax, ay] = points[a]!;
    const [bx, by] = points[b]!;
    const dx = bx - ax;
    const dy = by - ay;
    const length2 = dx * dx + dy * dy;
    let best = -1;
    let index: number | null = null;
    for (let k = a + 1; k < b; k++) {
      const [px, py] = points[k]!;
      let f = length2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / length2 : 0;
      f = Math.min(Math.max(f, 0), 1);
      const gap = Math.hypot(px - ax - f * dx, py - ay - f * dy);
      if (gap > best) [best, index] = [gap, k];
    }
    if (index !== null && best > tolerance) {
      keep[index] = true;
      stack.push([a, index], [index, b]);
    }
  }
  return keep.flatMap((k, i) => (k ? [i] : []));
}
